import { Component, ElementRef, Inject, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { LocalStorageService } from "../../shared/local-storage.service";
import { Passenger, PassengerImage } from "../../shared/models/passenger.model";
import { Country, SharedService } from "../../shared/shared.service";
import { PassengersService } from "../passengers.service";
import moment from "moment";
import { Observable } from "rxjs";
import { environment } from "projects/htl-part/src/environments/environment";
import { MatTabGroup } from "@angular/material/tabs";
import { ReturnStatement } from "@angular/compiler";

@Component({
    selector: "app-update-passenger-dialog",
    templateUrl: "./update-passenger-dialog.component.html",
    styleUrls: ["./update-passenger-dialog.component.scss"],
    standalone: false,
})
export class UpdatePassengerDialogComponent implements OnInit {
    @ViewChild("tabGroup", { static: false }) tabGroup: MatTabGroup;
    @ViewChild("fileInput", { static: false }) fileInput: ElementRef;

    public createMode = true;
    public addDNewPassengerCheck = false;
    public passengerFormGroup: FormGroup;
    public maxBirthDay = new Date();
    public countries: Country[] = [];
    public passengerImages: PassengerImage[] = [];
    public imagePathF: string | ArrayBuffer;
    public imagePathB: string | ArrayBuffer;
    public documentImageToUploadF: File = null;
    public documentImageToUploadB: File = null;

    public documentImageFront: FormControl = new FormControl("");
    public documentImageBack: FormControl = new FormControl("");

    constructor(
        public dialogRef: MatDialogRef<UpdatePassengerDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        private _passengerObject: {
            passengerId: number;
        },
        private _passengersService: PassengersService,
        private _localStorageService: LocalStorageService,
        private _formBuilder: FormBuilder,
        private _snackBar: MatSnackBar,
        private _translate: TranslateService,
        private _sharedService: SharedService,
    ) {
        const appSessionObject = this._localStorageService.getAppLocalStorageObject();
        this.passengerFormGroup = this._formBuilder.group({
            id: [""],
            firstName: ["", Validators.required],
            lastName: ["", Validators.required],
            gender: [""],
            dateOfBirth: [""],
            idCardNumber: [""],
            nationalityId: [""],
            status: [1],
        });
        this.countries = appSessionObject.countries;
    }

    ngOnInit(): void {
        const passengerList = this._passengersService.getPassengers();
        if (this._passengerObject.passengerId) {
            this.createMode = false;
            const currentPassenger = passengerList.filter((pass) => pass.id.toString() === this._passengerObject.passengerId.toString())[0];
            this._passengersService.getImagesByPassengerId(this._passengerObject.passengerId).subscribe((result) => {
                this.passengerImages = result;
                if (result[0]) {
                    this.imagePathF = environment.imageServerUrl + result[0].path;
                }
                if (result[1]) {
                    this.imagePathB = environment.imageServerUrl + result[1].path;
                }
            });
            this.passengerFormGroup.patchValue(currentPassenger);
        }
    }

    onFormSubmit() {
        this.documentImageFront.setErrors(null);
        this.documentImageBack.setErrors(null);
        if (this.passengerFormGroup.valid) {
            const formObject = { ...this.passengerFormGroup.value } as Passenger;
            if (formObject.dateOfBirth) {
                formObject.dateOfBirth = moment(formObject.dateOfBirth).format("YYYY-MM-DD");
            }
            let obs: Observable<Passenger>;
            if (formObject.id) {
                obs = this._passengersService.updatePassenger(formObject);
            } else {
                obs = this._passengersService.insertPassenger(formObject);
            }
            obs.subscribe((result) => {
                if (this.documentImageToUploadF) {
                    this._passengersService.uploadPassengerIdCardImage(result.id, this.documentImageToUploadF).subscribe(() => {
                        this._handleSuccess();
                    });
                } else {
                    this._handleSuccess();
                }
                if (this.documentImageToUploadB) {
                    this._passengersService.uploadPassengerIdCardImage(result.id, this.documentImageToUploadB).subscribe(() => {
                        this._handleSuccess();
                    });
                } else {
                    this._handleSuccess();
                }
            });
        } else {
            const errors = this._sharedService.checkIfFormGroupHasError(this.passengerFormGroup);
            errors.map((error) => {
                this.passengerFormGroup.get(error.key).setErrors({ required: true });
                this.passengerFormGroup.get(error.key).markAsTouched();
            });
        }
    }

    private _handleSuccess() {
        this.tabGroup.selectedIndex = 0;
        if (this.addDNewPassengerCheck) {
            this.passengerFormGroup.reset({ status: 1 });
            this.imagePathF = null;
            this.imagePathB = null;
            // this.signatureImageToUpload = null;
        } else {
            this.dialogRef.close();
        }
        this._snackBar.open(this._translate.instant("GENERAL.savedSuccessMsg"), this._translate.instant("GENERAL.close"), {
            duration: 3000,
        });
    }

    handleImageFileInputF(event) {
        const files = event.target.files;
        this.documentImageFront.setErrors(null);
        if (files.length === 0) {
            return;
        }
        if (files.length > 1) {
            return;
        }
        const mimeType = files[0].type;
        if (mimeType.match(/[\/.]gif|jpe?g|tiff?|png|webp|bmp$/i) == null) {
            this.documentImageFront.setErrors({ wrongFileType: true });
            return;
        }
        if (files[0].size > 5000000) {
            this.documentImageFront.setErrors({ maxFileSize: true });
            return;
        }
        const reader = new FileReader();
        this.documentImageToUploadF = files.item(0);
        reader.readAsDataURL(files[0]);
        reader.onload = (_event) => {
            this.imagePathF = reader.result;
        };
    }

    handleImageFileInputB(event) {
        const files = event.target.files;
        this.documentImageBack.setErrors(null);
        if (files.length === 0) {
            return;
        }
        if (files.length > 1) {
            return;
        }
        const mimeType = files[0].type;
        if (mimeType.match(/[\/.]gif|jpe?g|tiff?|png|webp|bmp$/i) == null) {
            this.documentImageBack.setErrors({ wrongFileType: true });
            return;
        }
        if (files[0].size > 5000000) {
            this.documentImageBack.setErrors({ maxFileSize: true });
            return;
        }
        const reader = new FileReader();
        this.documentImageToUploadB = files.item(0);
        reader.readAsDataURL(files[0]);
        reader.onload = (_event) => {
            this.imagePathB = reader.result;
        };
    }

    removeChoosenImage(imageIndex: number) {
        this.documentImageFront.setErrors(null);
        this.documentImageBack.setErrors(null);
        if (!this.passengerImages[imageIndex]) {
            if (imageIndex === 0) {
                this.documentImageToUploadF = null;
                this.imagePathF = null;
            } else {
                this.documentImageToUploadB = null;
                this.imagePathB = null;
            }
        } else {
            this._passengersService.deletePassengerIdCardImage(this.passengerImages[imageIndex].id, this._passengerObject.passengerId).subscribe(
                (result) => {
                    if (imageIndex === 0) {
                        this.documentImageToUploadF = null;
                        this.imagePathF = null;
                    } else {
                        this.documentImageToUploadB = null;
                        this.imagePathB = null;
                    }
                },
                (error) => {
                    console.log(error);
                },
            );
        }
    }
}
